
import { Vue, Component, Prop } from 'vue-property-decorator';
import CardBodyItem from '@/components/card/CardBodyItem.vue';
import ResourceCards from '@/components/card/ResourceCards.vue';
import ProjectChip from '@/components/managers/ProjectChip.vue';
import TableAvatar from '@/components/table/TableAvatar.vue';
import TableIcon from '@/components/table/table-icon.vue';
import ContractorChips from '@/components/companyProjects/ContractorChips.vue';
import ResourceAvatar from '@/components/card/ResourceAvatar.vue';
import ItemChipsWithMore from '@/components/common/ItemChipsWithMore.vue';
import ColorIconContainer from '@/components/icons/ColorIconContainer.vue';
import UiButton from '@/components/button/ui-button.vue';
import ChatIcon from '@/components/icons/ChatIconNew.vue';
import ChatIconHover from '@/components/icons/ChatIconHoverNew.vue';
import ManagerInvitationDisabledTooltip from '@/components/managers/ManagerInvitationDisabledTooltip.vue';
import ManagerResendInvitation from '@/components/invite/ManagerResendInvitation.vue';
import ResendInvitationPlaceholder from '@/components/invite/ResendInvitationPlaceholder.vue';
import Badge from "@/components/common/BadgeNew.vue";

import { Manager, Project, User } from '@/generated/graphql';
import RouteNames from '@/entities/RouteNames';
import RouteParams from '@/entities/RouteParams';
import ManagerStatus from '@/entities/manager/ManagerStatus';
import { emailStatusText, managerSuspendedText } from '@/data/userStatuses';
import BadgeColor from '@/entities/BadgeColor';
import CardBodyItemLink from '@/components/card/CardBodyItemLink.vue';
import getLocationInfo from '@/helpers/locations/getLocationInfo';
import ActionSection from '@/components/card/ActionSection.vue';
import CountryFlag from 'vue-country-flag';
import getCountryCode from '@/utils/getCountryCode';

interface IManagerProject {
	id: string;
	name: string;
}

interface IActionDefault {
	leftTitle: string;
	leftValue: number | string;
	rightTitle: string;
	rightValue: number | string;
}

interface IActionError {
	title: string;
	description: number | string;
	actionButton: string;
}

interface IActionSection {
	state: boolean;
	default: IActionDefault;
	error: IActionError;
}

@Component({
	components: {
		CardBodyItemLink,
		Badge,
		ManagerResendInvitation,
		CardBodyItem,
		ProjectChip,
		ResourceCards,
		ChatIconHover,
		TableAvatar,
		TableIcon,
		ContractorChips,
		ResourceAvatar,
		ItemChipsWithMore,
		ColorIconContainer,
		UiButton,
		ChatIcon,
		ManagerInvitationDisabledTooltip,
		ResendInvitationPlaceholder,
		ActionSection,
		CountryFlag,
	},
})
export default class ManagerCard extends Vue {
	@Prop({ type: Object, required: true }) readonly manager!: Manager;
	@Prop({ type: Object, required: true }) readonly currentUser!: User;
	@Prop({ type: Number, required: true })
	readonly activeManagersCount!: number;
	@Prop({ type: Number, required: true })
	readonly accessAllowedManagersCount!: number;
	@Prop({ type: Boolean, required: true }) readonly getRoleAdmin!: boolean;

	BadgeColor: typeof BadgeColor = BadgeColor;
	routes = RouteNames;
	emailStatusText: string = emailStatusText;
	managerSuspendedText: string = 'Account disabled';
	RouteNames: typeof RouteNames = RouteNames;
	isHovered: boolean = false;

	get contractorsRequestCount(): number {
		return this.manager.contractors_aggregate.aggregate?.count || 0;
	}

	get isEmailUnconfirmed(): boolean {
		return !this.manager.user.confirmed;
	}

	get isResendInvitationShown(): boolean {
		return this.isEmailUnconfirmed && !this.isManagerSuspended;
	}

	get allowedActivateManagers(): boolean {
		if (this.isManagersCountUnlimited) {
			return true;
		}

		return this.accessAllowedManagersCount > this.activeManagersCount;
	}

	get isManagersCountUnlimited(): boolean {
		return !this.currentUser.subscription_required;
	}

	get country() {
		return getCountryCode(this.manager.user.profile.country);
	}

	get skills() {
		const skills = this.manager.user.profile.skills;
		return skills;
	}

	get contractors() {
		if (!this.manager.contractors) {
			return [];
		}
		return this.manager.contractors.map(({ contractor }) => {
			const { id, user } = contractor;
			const { given_name, family_name } = user;

			return { id, name: `${given_name} ${family_name}` };
		});
	}

	get contractorsNumber(): number {
		return this.contractors.length;
	}

	get displayProjects(): boolean {
		return this.managerProjects.length > 0;
	}

	get managerAvatar(): string | undefined {
		return this.manager.user.avatar || undefined;
	}

	get managerInitials(): string {
		return `${this.manager.user.given_name![0]}${
			this.manager.user.family_name![0]
		}`;
	}

	get isManagerSuspended(): boolean {
		return this.manager.status === ManagerStatus.SUSPENDED;
	}

	get managerFullName(): string {
		return `${this.manager.user.given_name} ${this.manager.user.family_name}`;
	}

	get isCardDisabled(): boolean {
		return this.isManagerSuspended || this.isEmailUnconfirmed;
	}

	get managerJobTitle(): string | undefined {
		if (!this.manager.user.profile?.job_title) {
			return undefined;
		}
		return this.manager.user.profile.job_title
			.split(' ')
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(' ');
	}

	get managerLocation(): string {
		return getLocationInfo(this.manager.user.profile!);
	}

	get managerProjects(): IManagerProject[] {
		const projects: Project[] = this.manager.projects || [];

		return projects.map((p) => {
			return {
				id: p.id,
				name: p.name ? p.name : '',
			};
		});
	}

	get managerLink(): RouteParams<{ id: string }> {
		return {
			name: RouteNames.MANAGER_ABOUT,
			params: { id: this.manager.id },
		};
	}

	get contractorsLink(): RouteParams<{ id: string }> {
		return {
			name: RouteNames.MANAGER_CONTRACTORS,
			params: { id: this.manager.id },
		};
	}

	get projectsLink(): RouteParams<{ id: string }> {
		return {
			name: RouteNames.MANAGER_PROJECTS,
			params: { id: this.manager.id },
		};
	}

	onActivateManager(): undefined {
		if (this.allowedActivateManagers) {
			this.$emit('activate', this.manager.id);
			return;
		}

		this.$router.push({
			name: RouteNames.PRICING,
		});
	}

	isThereAnyErrorMessage(): boolean {
		if (this.isManagerSuspended || this.isEmailUnconfirmed) return true;
		return false;
	}

	get getActionSectionValues(): IActionSection {
		// use this function to get the values for the action section

		const actionSectionResult: IActionSection = {
			state: this.isThereAnyErrorMessage(),
			default: {
				leftTitle: 'Active Client',
				leftValue: this.managerProjects.length,
				rightTitle: 'Resources',
				rightValue: this.contractors.length,
			},
			error: {
				title:
					this.isEmailUnconfirmed && !this.isManagerSuspended
						? this.emailStatusText
						: this.managerSuspendedText,
				description: '',
				actionButton:
					this.isEmailUnconfirmed && !this.isManagerSuspended
						? 'resend'
						: 'activate',
			},
		};
		return actionSectionResult;
	}
}
